import React, { useMemo, useState } from "react";
import { Pause, Play, SkipBack, SkipForward } from "lucide-react";

const sevColor = (s) => ({
  critical: "hsl(var(--destructive))",
  high: "hsl(var(--destructive))",
  medium: "hsl(var(--warning))",
  low: "hsl(var(--primary))",
}[s] || "hsl(var(--muted-foreground))");

export const IncidentTimeline = ({ alerts = [] }) => {
  const [playing, setPlaying] = useState(true);
  const [scrub, setScrub] = useState(100);

  // Build a normalized timeline from the most recent N alerts
  const points = useMemo(() => {
    const list = alerts.slice(0, 60);
    if (list.length === 0) return [];
    const times = list.map((a) => new Date(a.ts).getTime());
    const max = Math.max(...times);
    const min = Math.min(...times);
    const span = Math.max(1, max - min);
    return list.map((a) => ({
      ...a,
      pos: ((new Date(a.ts).getTime() - min) / span) * 100,
    }));
  }, [alerts]);

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
        <span className="h-widget">Incident Timeline · Replay</span>
        <div className="flex items-center gap-1">
          <button className="btn-tac !px-2 !py-1" data-testid="timeline-skip-back"><SkipBack className="w-3 h-3" /></button>
          <button
            className="btn-tac-primary !px-2 !py-1"
            onClick={() => setPlaying((p) => !p)}
            data-testid="timeline-play-toggle"
          >
            {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            {playing ? "Live" : "Paused"}
          </button>
          <button className="btn-tac !px-2 !py-1" data-testid="timeline-skip-forward"><SkipForward className="w-3 h-3" /></button>
        </div>
      </div>

      <div className="flex-1 px-4 py-2 flex flex-col justify-center min-h-0">
        <div className="relative h-10">
          {/* track */}
          <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
          <div
            className="absolute top-1/2 h-px bg-[hsl(var(--primary))]"
            style={{ left: 0, width: `${scrub}%` }}
          />
          {/* tick marks */}
          {[0, 25, 50, 75, 100].map((t) => (
            <div key={t} className="absolute top-1/2 -translate-y-1/2" style={{ left: `${t}%` }}>
              <div className="w-px h-3 bg-border" />
              <div className="font-mono text-[9px] text-muted-foreground -translate-x-1/2 mt-1">
                -{Math.round((100 - t) * 6 / 10)}m
              </div>
            </div>
          ))}
          {/* event dots */}
          {points.map((p) => (
            <div
              key={p.id}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group"
              style={{ left: `${p.pos}%` }}
              data-testid={`timeline-event-${p.id}`}
              title={`${p.title} · ${p.zone_name}`}
            >
              <div
                className="w-1.5 h-1.5"
                style={{ background: sevColor(p.severity) }}
              />
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-[hsl(var(--surface-elevated))] border border-border text-[10px] px-1.5 py-0.5 font-mono">
                {p.title}
              </div>
            </div>
          ))}
          {/* handle */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1.5 h-7 bg-[hsl(var(--primary))]"
            style={{ left: `${scrub}%` }}
          />
        </div>
        <input
          type="range" min="0" max="100" value={scrub}
          onChange={(e) => setScrub(Number(e.target.value))}
          className="w-full mt-3 accent-[hsl(var(--primary))]"
          data-testid="timeline-scrubber"
        />
        <div className="mt-1 flex justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <span>T-60m</span><span>T-45m</span><span>T-30m</span><span>T-15m</span><span>Now</span>
        </div>
      </div>
    </div>
  );
};

export default IncidentTimeline;
